import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";

const ResponsiveTable = ({
  data = [],
  columns = [],
  actions = [],
  loading = false,
  emptyMessage = "Nenhum item encontrado",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 200,
        }}
      >
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 200,
          textAlign: "center",
        }}
      >
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  // Renderização para mobile
  if (isMobile) {
    return (
      <Box className="mobile-table-container">
        {data.map((item, index) => (
          <Box key={index} className="mobile-card">
            <Box className="mobile-card-header">
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {item[columns[0]?.field] || `Item ${index + 1}`}
              </Typography>
              {actions.length > 0 && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  {actions.map((action, actionIndex) => {
                    const isDisabled = action.disabled && action.disabled(item);
                    return (
                      <IconButton
                        key={actionIndex}
                        size="small"
                        onClick={() => !isDisabled && action.onClick(item)}
                        color={action.color || "default"}
                        title={action.label}
                        disabled={isDisabled}
                      >
                        {action.icon}
                      </IconButton>
                    );
                  })}
                </Box>
              )}
            </Box>
            <Box className="mobile-card-content">
              {columns.slice(1).map((column, colIndex) => {
                let value = item[column.field];

                // Aplicar formatação se existir (mesmo para valores falsy)
                if (column.format) {
                  value = column.format(value, item);
                }

                // Renderizar chips se especificado
                if (column.chip && value) {
                  const chipColor =
                    typeof column.chipColor === "function"
                      ? column.chipColor(item[column.field])
                      : item[column.field]
                        ? "success"
                        : "default";

                  value = (
                    <Chip
                      label={value}
                      size="small"
                      color={chipColor}
                      variant={column.chipVariant || "filled"}
                    />
                  );
                }

                return (
                  <Box key={colIndex} className="mobile-card-row">
                    <span className="mobile-card-label">{column.label}:</span>
                    <span className="mobile-card-value">{value || "-"}</span>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // Renderização para desktop
  return (
    <Box className="desktop-table-container">
      <TableContainer component={Paper} className="table-responsive">
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: 600,
                    minWidth: column.minWidth || "auto",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {actions.length > 0 && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    textAlign: "center",
                    width: 120,
                  }}
                >
                  Ações
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={index} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
                {columns.map((column, colIndex) => {
                  let value = item[column.field];

                  // Aplicar formatação se existir (mesmo para valores falsy)
                  if (column.format) {
                    value = column.format(value, item);
                  }

                  // Renderizar chips se especificado
                  if (column.chip && value) {
                    const chipColor =
                      typeof column.chipColor === "function"
                        ? column.chipColor(item[column.field])
                        : item[column.field]
                          ? "success"
                          : "default";

                    value = (
                      <Chip
                        label={value}
                        size="small"
                        color={chipColor}
                        variant={column.chipVariant || "filled"}
                      />
                    );
                  }

                  return <TableCell key={colIndex}>{value || "-"}</TableCell>;
                })}
                {actions.length > 0 && (
                  <TableCell sx={{ textAlign: "center" }}>
                    <Box
                      sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                    >
                      {actions.map((action, actionIndex) => {
                        const isDisabled =
                          action.disabled && action.disabled(item);
                        return (
                          <IconButton
                            key={actionIndex}
                            size="small"
                            onClick={() => !isDisabled && action.onClick(item)}
                            color={action.color || "default"}
                            title={action.label}
                            disabled={isDisabled}
                          >
                            {action.icon}
                          </IconButton>
                        );
                      })}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ResponsiveTable;
